/**
 * Task #245 — Tier 1 local fuzzy index for the Spotlight quick search.
 *
 * A tiny custom fuzzy matcher — we deliberately avoid pulling fuse.js
 * into the bundle because the matching surface here is small and a
 * substring + chained-token search covers the acceptance criteria. The
 * function returns the same `[start, end]` index ranges that the
 * server tier produces, so `HighlightedText` is renderer-tier-agnostic.
 *
 * Phase 2: each project carries `year: number` (the full calendar year
 * from the `years` table); a token that is exactly 4 digits matches
 * against it. The strict /^\d{4}$/ guard keeps generic numeric tokens
 * like "26" out of the year branch.
 *
 * Phase 3: the matcher accumulates EVERY successful token hit rather
 * than collapsing to a single bestHit. Tokens that hit the same field
 * merge their `matchIndices`; tokens that hit different fields surface
 * as `additionalMatches[]` so the row can render a chip per matched
 * field. The chained-token branch ALSO surfaces the client narrowing
 * as a clientName match — without this, typing "wer 26" silently lost
 * the "wer → client Werbefilm" half of the hit.
 */

import type { MatchReason, MatchReasonField, SearchHit } from './types';

export interface IndexClient {
  id: string;
  name: string;
}

export interface IndexProjectArtist {
  shortCode: string;
  firstName: string | null;
  lastName: string | null;
  isLead: boolean;
}

export interface IndexProject {
  id: string;
  projectName: string;
  clientId: string;
  clientName: string;
  thumbnailUrl: string | null;
  searchKeywords: string[];
  /** Phase 2 — full calendar year (e.g. 2026). Server populates from `years.fullYear`. */
  year: number;
  /** Task #257 — lead + additional artists for surface-name search. */
  artists?: IndexProjectArtist[];
}

export interface LocalIndexPayload {
  clients: IndexClient[];
  projects: IndexProject[];
}

function indicesFor(haystack: string, needle: string): Array<[number, number]> {
  if (!needle) return [];
  const idx = haystack.toLowerCase().indexOf(needle.toLowerCase());
  if (idx < 0) return [];
  return [[idx, idx + needle.length - 1]];
}

interface TokenHit {
  reason: MatchReason;
  score: number;
}

/**
 * Score a project against a single query token, returning the best
 * matchReason + score combination. Score ranking mirrors the server
 * tier so a merged list ranks consistently.
 */
function scoreProjectAgainstToken(
  project: IndexProject,
  token: string,
): TokenHit | null {
  if (!token) return null;
  const lowered = token.toLowerCase();

  // Phase 2 — exact 4-digit year match. Highest priority slot just
  // below project ID prefix; surfaces only when the token IS a 4-digit
  // year. Generic "26" never reaches this branch because of the regex
  // guard.
  if (/^\d{4}$/.test(token) && String(project.year) === token) {
    return {
      reason: {
        field: 'year',
        value: String(project.year),
        matchIndices: [[0, 3]],
      },
      score: 950,
    };
  }

  // Direct hits, mirror server priority + scores.
  if (project.id.toLowerCase().startsWith(lowered)) {
    return { reason: { field: 'projectNumber', value: project.id, matchIndices: indicesFor(project.id, token) }, score: 1000 };
  }
  if (project.projectName.toLowerCase().startsWith(lowered)) {
    return { reason: { field: 'projectName', value: project.projectName, matchIndices: indicesFor(project.projectName, token) }, score: 900 };
  }
  if (project.projectName.toLowerCase().includes(lowered)) {
    return { reason: { field: 'projectName', value: project.projectName, matchIndices: indicesFor(project.projectName, token) }, score: 800 };
  }
  if (project.clientName.toLowerCase().includes(lowered)) {
    return { reason: { field: 'clientName', value: project.clientName, matchIndices: indicesFor(project.clientName, token) }, score: 600 };
  }
  if (project.id.toLowerCase().includes(lowered)) {
    return { reason: { field: 'projectNumber', value: project.id, matchIndices: indicesFor(project.id, token) }, score: 550 };
  }
  for (const kw of project.searchKeywords ?? []) {
    if (kw.toLowerCase().includes(lowered)) {
      return { reason: { field: 'keyword', value: kw, matchIndices: indicesFor(kw, token) }, score: 400 };
    }
  }
  // Task #257 — artist branch. Lead and additional artists carry the
  // same `firstName/lastName/shortCode` surface; try each in priority
  // order. Lead artists rank slightly above additional artists; both
  // sit below the keyword score (400) so a project literally named or
  // keyworded "Marcel" still beats every artist-Marcel hit.
  if (Array.isArray(project.artists) && project.artists.length > 0) {
    for (const a of project.artists) {
      const fullName = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim();
      // Match priority: firstName → lastName → shortCode. The chip
      // value mirrors the source of the match — when the token hit
      // the short code, the chip shows the short code (e.g. "MCL")
      // with its highlight; when it hit a name, the chip shows the
      // full display name. Falling back to shortCode when full name
      // is empty.
      let value: string | null = null;
      if (a.firstName && a.firstName.toLowerCase().includes(lowered)) {
        value = fullName || a.firstName;
      } else if (a.lastName && a.lastName.toLowerCase().includes(lowered)) {
        value = fullName || a.lastName;
      } else if (a.shortCode && a.shortCode.toLowerCase().includes(lowered)) {
        value = a.shortCode;
      }
      if (!value) continue;
      return {
        reason: {
          field: a.isLead ? 'leadArtist' : 'artist',
          value,
          matchIndices: indicesFor(value, token),
        },
        score: a.isLead ? 350 : 320,
      };
    }
  }
  return null;
}

/**
 * Merge a per-token TokenHit into an accumulator keyed by field. Hits
 * on the same field collapse their `matchIndices`; hits on different
 * fields stay separate so the row can render multiple chips.
 *
 * Same-field index merging is done by simple sort+dedup — the renderer
 * (`HighlightedText`) collapses overlaps defensively, so duplicate or
 * adjacent ranges are tolerated, but we still sort for predictable
 * output.
 */
function mergeTokenHits(hits: TokenHit[]): { primary: TokenHit; additional: MatchReason[] } | null {
  if (hits.length === 0) return null;
  // Group by field, keeping the highest-scored hit per field as the
  // representative (its `value` already matches the field — any token
  // hitting the same field hits the same haystack string).
  const byField = new Map<MatchReasonField, { score: number; value: string; indices: Array<[number, number]> }>();
  for (const h of hits) {
    const existing = byField.get(h.reason.field);
    if (!existing) {
      byField.set(h.reason.field, {
        score: h.score,
        value: h.reason.value,
        indices: [...h.reason.matchIndices],
      });
    } else {
      existing.score = Math.max(existing.score, h.score);
      // Same field on the same project ⇒ same `value`. Just append
      // indices and dedup later.
      for (const range of h.reason.matchIndices) existing.indices.push(range);
    }
  }
  // Build per-field MatchReasons with sorted+deduped indices.
  const byScore: Array<{ field: MatchReasonField; reason: MatchReason; score: number }> = [];
  for (const [field, agg] of byField) {
    const sorted = [...agg.indices].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    // Drop exact duplicates.
    const deduped: Array<[number, number]> = [];
    for (const r of sorted) {
      const last = deduped[deduped.length - 1];
      if (!last || last[0] !== r[0] || last[1] !== r[1]) deduped.push(r);
    }
    byScore.push({
      field,
      reason: { field, value: agg.value, matchIndices: deduped },
      score: agg.score,
    });
  }
  // Pick the highest-scored field as the primary reason; the rest
  // become `additionalMatches`.
  byScore.sort((a, b) => b.score - a.score);
  const primaryEntry = byScore[0];
  return {
    primary: { reason: primaryEntry.reason, score: primaryEntry.score },
    additional: byScore.slice(1).map(e => e.reason),
  };
}

function buildHit(
  project: IndexProject,
  primary: TokenHit,
  additional: MatchReason[],
  combinedScore: number,
): SearchHit {
  return {
    id: project.id,
    type: 'project',
    projectId: project.id,
    projectName: project.projectName,
    clientId: project.clientId,
    clientName: project.clientName,
    thumbnailUrl: project.thumbnailUrl,
    matchReason: primary.reason,
    additionalMatches: additional.length > 0 ? additional : undefined,
    score: combinedScore,
  };
}

/**
 * Run the chained-token search:
 *   - Token 1 narrows the client universe (any client whose name
 *     matches the first token bounds the result set; if no client
 *     matches token 1 we fall back to flat AND-matching).
 *   - Remaining tokens then narrow projects whose clientId is in the
 *     narrowed set, or — fallback — match across all visible projects
 *     with AND-semantics across tokens.
 *
 * Phase 3: when chained narrowing fires, the matched client name is
 * surfaced as one of the hit's match reasons (either primary or
 * additional, depending on its score relative to the per-project token
 * hits). Without this, the "wer" half of "wer 26" disappeared from
 * the rendered chips.
 */
export function searchLocalIndex(
  index: LocalIndexPayload,
  query: string,
  limit: number = 50,
): SearchHit[] {
  const q = query.trim();
  if (!q) return [];

  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  // byProjectId merges chained + flat-AND hits, keeping the higher-scored
  // entry per project. Without the flat-AND fallback, a query like
  // "nord wer" — where token 1 happens to match a client different from
  // the user's intent (or none at all) — would return an empty list
  // even though `wer nord` works perfectly.
  const byProjectId = new Map<string, { hit: SearchHit; score: number }>();

  const consider = (entry: { hit: SearchHit; score: number }) => {
    const existing = byProjectId.get(entry.hit.projectId);
    if (!existing || entry.score > existing.score) {
      byProjectId.set(entry.hit.projectId, entry);
    }
  };

  // Pass 1: chained narrowing. Try EACH token as the potential client
  // anchor (not just token 0) so the result set is identical regardless
  // of token order — `wer nord` and `nord wer` both anchor on "wer" and
  // produce identical scores.
  if (tokens.length >= 2) {
    for (let anchorIdx = 0; anchorIdx < tokens.length; anchorIdx++) {
      const anchorTok = tokens[anchorIdx];
      const anchorLower = anchorTok.toLowerCase();
      const matchedClients = index.clients.filter(c =>
        c.name.toLowerCase().includes(anchorLower),
      );
      if (matchedClients.length === 0) continue;

      const candidateProjects = index.projects.filter(p =>
        matchedClients.some(c => c.id === p.clientId),
      );
      const remainingTokens = tokens.filter((_, i) => i !== anchorIdx);

      for (const project of candidateProjects) {
        const perTokenHits: TokenHit[] = [];
        // Seed a clientName hit so the rendered row shows the client
        // chip even though the anchor token was "consumed" by narrowing.
        perTokenHits.push({
          reason: {
            field: 'clientName',
            value: project.clientName,
            matchIndices: indicesFor(project.clientName, anchorTok),
          },
          score: 600,
        });

        let matchedAll = true;
        for (const tok of remainingTokens) {
          const tokenHit = scoreProjectAgainstToken(project, tok);
          if (!tokenHit) {
            matchedAll = false;
            break;
          }
          perTokenHits.push(tokenHit);
        }
        if (!matchedAll) continue;

        const merged = mergeTokenHits(perTokenHits);
        if (!merged) continue;

        let combinedScore = merged.primary.score;
        for (const a of merged.additional) combinedScore += fieldBaseScore(a.field);
        if (remainingTokens.length === 1) combinedScore += 100;

        consider({
          hit: buildHit(project, merged.primary, merged.additional, combinedScore),
          score: combinedScore,
        });
      }
    }
  }

  // Pass 2 — flat-AND fallback across the FULL project list. Always
  // runs (in addition to the chained pass), and merges per projectId
  // keeping the higher score. When chained found the same project, its
  // +100 bonus keeps it on top; when it didn't, flat-AND can still
  // surface a hit instead of an empty list.
  for (const project of index.projects) {
    const perTokenHits: TokenHit[] = [];
    let matchedAllTokens = true;
    for (const tok of tokens) {
      const tokenHit = scoreProjectAgainstToken(project, tok);
      if (!tokenHit) {
        matchedAllTokens = false;
        break;
      }
      perTokenHits.push(tokenHit);
    }
    if (!matchedAllTokens) continue;
    const merged = mergeTokenHits(perTokenHits);
    if (!merged) continue;
    let combinedScore = merged.primary.score;
    for (const a of merged.additional) combinedScore += fieldBaseScore(a.field);
    consider({
      hit: buildHit(project, merged.primary, merged.additional, combinedScore),
      score: combinedScore,
    });
  }

  const scored = Array.from(byProjectId.values());
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map(s => s.hit);
}

/** Rough field-base for the additive contribution of an additional match. */
function fieldBaseScore(field: MatchReasonField): number {
  switch (field) {
    case 'projectNumber': return 550;
    case 'projectName': return 800;
    case 'year': return 950;
    case 'clientName': return 600;
    case 'keyword': return 400;
    case 'leadArtist': return 350;
    case 'artist': return 320;
    default: return 400;
  }
}
